// The coaching call types a visitor may book on our own domain.
//
// This lives OUTSIDE the booking widget, which is a "use client" module. Shared
// data a server component reads belongs in a plain module both sides import,
// rather than crossing a client boundary to get there.
//
// Honest note on why this file was written: every /book/<type> URL was 404ing
// and the client boundary was the suspect. It was not the cause. The real cause
// was the dev server, which never registered the new [type] directory because
// it was created while the server was running, and a restart did not clear it
// either. Adding any new file under /book forced the rescan and all four routes
// resolved at once. The move is still correct on its own merits, so it stays,
// but it did not fix anything and should not be cited as the fix.
//
// Keyed by the slug in /book/<slug>, deliberately the same slug Cal uses, so a
// link is readable and there is one name per type. An unknown slug 404s rather
// than falling through to the application call: a man who paid for a 60-minute
// call must never be quietly handed the free fifteen.

// THE APPLICATION CALL HAS ONE LIVE SLUG, `application-call`: the slug the
// Cal.com event type carries, the link we hand out, and the value a new
// booking arrives under. `consultation-call` no longer exists on Cal.com; it
// is recognised only so historic bookings and webhook rows that carry it still
// classify as the application call.
//
// The rule: every place that READS a stored slug accepts both, through
// `isApplicationCallSlug` or the set below; every place that WRITES a slug or
// a link uses `APPLICATION_CALL_SLUG` alone.
export const APPLICATION_CALL_SLUG = "application-call"
export const LEGACY_APPLICATION_CALL_SLUG = "consultation-call"
export const APPLICATION_CALL_SLUGS: readonly string[] = [APPLICATION_CALL_SLUG, LEGACY_APPLICATION_CALL_SLUG]
export const APPLICATION_CALL_SLUG_SET: ReadonlySet<string> = new Set(APPLICATION_CALL_SLUGS)

/** Is this Cal event-type slug the application call, under either name? Trims
 * and lowercases, so a slug read back off a webhook or a cache row matches. */
export function isApplicationCallSlug(slug: string | null | undefined): boolean {
  return APPLICATION_CALL_SLUG_SET.has((slug ?? "").trim().toLowerCase())
}

/** The Cal link the booking widget embeds: always the canonical slug. */
export const APPLICATION_CAL_LINK = `coldapproach/${APPLICATION_CALL_SLUG}`

// THE PAID COACHING CALLS. A man on one of these has already bought, so every
// pre-call surface owes him a different conversation: he is never told he is
// booked for an application call, and never shown the video about which
// program fits (he already picked one).
export const PAID_COACHING_CALL_SLUGS: readonly string[] = ["30-minute-coaching-call", "60-minute-coaching-call"]
const PAID_COACHING_CALL_SLUG_SET: ReadonlySet<string> = new Set(PAID_COACHING_CALL_SLUGS)

export function isPaidCoachingCallSlug(slug: string | null | undefined): boolean {
  return PAID_COACHING_CALL_SLUG_SET.has((slug ?? "").trim().toLowerCase())
}

/**
 * Which of the two conversations a booking is, from the EVENT TYPE alone.
 *
 * Deliberately not from the call target's shape. The application call is by
 * phone, while paid coaching calls use Google Meet. The event-type slug says
 * which conversation it is; the target says how that booking connects.
 *
 * An UNRECOGNISED slug answers "application", which is what every surface did
 * before this function existed, so a one-off link or a manually created event
 * keeps its current copy rather than silently becoming a paid-client email.
 */
export type BookingCallKind = "application" | "paid_coaching"

export function bookingCallKind(slug: string | null | undefined): BookingCallKind {
  return isPaidCoachingCallSlug(slug) ? "paid_coaching" : "application"
}

/** "application call" / "coaching call" - the noun every pre-call surface uses
 * for this booking, so one booking is never two different things in two
 * different messages. */
export function callNounFor(slug: string | null | undefined): string {
  return bookingCallKind(slug) === "paid_coaching" ? "coaching call" : "application call"
}

// The application call is NOT in this map: /book already IS that page, so
// /book/application-call rendered a byte-identical twin. Both it and the
// legacy /book/consultation-call 308 to /book in next.config.ts, and this
// route serves only the PAID call types.
export const BOOKABLE_CALL_TYPES: Record<string, { calLink: string; title: string }> = {
  "30-minute-coaching-call": {
    calLink: "coldapproach/30-minute-coaching-call",
    title: "Book your 30-minute coaching call",
  },
  "60-minute-coaching-call": {
    calLink: "coldapproach/60-minute-coaching-call",
    title: "Book your 60-minute coaching call",
  },
}
